import { acquireAccessTokenOrRedirect } from '@/auth/authSession'
import { getSelectedTenantId } from '@/lib/tenantStorage'
import type { PagedResult, PaginationParams, WorkflowNodeErrorSummary, WorkflowNodeLogsResponse, WorkflowRun, WorkflowRunDetail, WorkflowRunFilters, WorkflowRunNodeContent, WorkflowRunNodeMemory } from '@/types'
import { client } from './client'

interface WorkflowRunQueryParams {
  search?: string
  status?: string[]
  triggeredFrom?: string
  definitionName?: string
  fromDate?: string
  toDate?: string
  sortBy?: string
  sortDirection?: string
  maxResultCount?: number
  skipCount?: number
}

function buildQueryParams(filters?: WorkflowRunFilters, pagination?: PaginationParams): WorkflowRunQueryParams {
  const params: WorkflowRunQueryParams = {}

  if (filters?.search) {
    params.search = filters.search
  }
  if (filters?.statuses?.length) {
    params.status = filters.statuses
  }
  if (filters?.definitionName) {
    params.definitionName = filters.definitionName
  }
  if (filters?.fromDate) {
    params.fromDate = filters.fromDate
  }
  if (filters?.toDate) {
    params.toDate = filters.toDate
  }
  if (filters?.sortBy) {
    params.sortBy = filters.sortBy
  }
  if (filters?.sortDirection) {
    params.sortDirection = filters.sortDirection
  }

  if (pagination?.maxResultCount) {
    params.maxResultCount = pagination.maxResultCount
  }
  if (pagination?.skipCount) {
    params.skipCount = pagination.skipCount
  }

  return params
}

export const workflowRunsApi = {
  getAll: (projectId: string, filters?: WorkflowRunFilters, pagination?: PaginationParams) =>
    client.get<PagedResult<WorkflowRun>>(`/projects/${projectId}/workflow-runs`, {
      params: buildQueryParams(filters, pagination),
    }).then(r => r.data),

  getById: (projectId: string, id: string) =>
    client.get<WorkflowRunDetail>(`/projects/${projectId}/workflow-runs/${id}`).then(r => r.data),

  cancel: (projectId: string, id: string) =>
    client.post<{ runId: string; status: string }>(`/projects/${projectId}/workflow-runs/${id}/cancel`).then(r => r.data),

  retry: (projectId: string, id: string) =>
    client.post<{ runId: string; status: string }>(`/projects/${projectId}/workflow-runs/${id}/retry`).then(r => r.data),

  getForWorkItem: (projectId: string, workItemId: string, filters?: WorkflowRunFilters, pagination?: PaginationParams) =>
    client.get<PagedResult<WorkflowRun>>(`/projects/${projectId}/work-items/${workItemId}/workflow-runs`, {
      params: buildQueryParams(filters, pagination),
    }).then(r => r.data),

  retryNode: (projectId: string, runId: string, nodeId: string, modifiedInputsJson?: string) =>
    client.post(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/retry`,
      modifiedInputsJson ? { modifiedInputsJson } : undefined
    ).then(r => r.data),

  skipNode: (projectId: string, runId: string, nodeId: string) =>
    client.post(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/skip`).then(r => r.data),

  getNodeLogs: (projectId: string, runId: string, nodeId: string) =>
    client.get<WorkflowNodeLogsResponse>(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/logs`).then(r => r.data),

  getPersistedNodeLogs: (projectId: string, runId: string, nodeId: string) =>
    client.get<WorkflowNodeLogsResponse>(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/logs/persisted`).then(r => r.data),

  getNodeErrorSummary: (projectId: string, runId: string, nodeId: string) =>
    client.get<WorkflowNodeErrorSummary>(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/error-summary`).then(r => r.data),

  getNodeContent: (projectId: string, runId: string, nodeId: string) =>
    client.get<WorkflowRunNodeContent>(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/content`).then(r => r.data),

  getNodeMemory: (projectId: string, runId: string, nodeId: string) =>
    client.get<WorkflowRunNodeMemory | null>(`/projects/${projectId}/workflow-runs/${runId}/nodes/${nodeId}/memory`)
      .then(r => r.data || null),
}

export async function streamWorkflowRun(
  projectId: string,
  runId: string,
  onEvent: (event: string, data: string, id: string | undefined) => void,
  signal?: AbortSignal,
  lastEventId?: string,
  onOpen?: () => void,
) {
  const token = await acquireAccessTokenOrRedirect()
  if (!token) return

  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
  const url = `${baseUrl}/projects/${projectId}/workflow-runs/${runId}/stream`

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  }

  const tenantId = getSelectedTenantId()
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId
  }

  if (lastEventId !== undefined) {
    headers['Last-Event-ID'] = lastEventId
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
    signal,
  })

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }

  onOpen?.()

  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent = ''
  let currentId: string | undefined

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (line.startsWith('id: ')) {
        currentId = line.slice(4).trim()
      } else if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim()
      } else if (line.startsWith('data: ') && currentEvent) {
        const data = line.slice(6).trim()
        onEvent(currentEvent, data, currentId)
        currentEvent = ''
        currentId = undefined
      }
    }
  }
}
